import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type SavedKind = "article" | "recipe" | "video";
export type SavedItem = { kind: SavedKind; id: string };

const kindSchema = z.enum(["article", "recipe", "video"]);
const itemSchema = z.object({ kind: kindSchema, id: z.string().uuid() });

/** Hent alle brugerens gemte elementer (article/recipe/video). */
export const getSavedItems = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("saved_items")
      .select("kind, item_id")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []).map((r: any) => ({
      kind: r.kind as SavedKind,
      id: r.item_id as string,
    }));
  });

/**
 * Merge lokale gemte elementer op i skyen (union) og returnér den flettede
 * liste. Idempotent – dubletter ignoreres.
 */
export const syncSavedItems = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ localItems: z.array(itemSchema) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    if (data.localItems.length > 0) {
      const rows = data.localItems.map((it) => ({
        user_id: context.userId,
        kind: it.kind,
        item_id: it.id,
      }));
      const { error } = await context.supabase
        .from("saved_items")
        .upsert(rows, {
          onConflict: "user_id,kind,item_id",
          ignoreDuplicates: true,
        });
      if (error) throw new Error(error.message);
    }
    const { data: rows, error: readErr } = await context.supabase
      .from("saved_items")
      .select("kind, item_id")
      .order("created_at", { ascending: false });
    if (readErr) throw new Error(readErr.message);
    return (rows ?? []).map((r: any) => ({
      kind: r.kind as SavedKind,
      id: r.item_id as string,
    }));
  });

/** Tilføj ét gemt element. */
export const addSavedItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => itemSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("saved_items")
      .upsert(
        [{ user_id: context.userId, kind: data.kind, item_id: data.id }],
        { onConflict: "user_id,kind,item_id", ignoreDuplicates: true },
      );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Fjern ét gemt element. */
export const removeSavedItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => itemSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("saved_items")
      .delete()
      .eq("user_id", context.userId)
      .eq("kind", data.kind)
      .eq("item_id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
